import * as React from 'react';
import Button from '../Button/Button';
import type { ResizableProps } from '../../studio/Resizable/Resizable';
import Resizable from '../../studio/Resizable/Resizable';
import type AppError from '../../../lib/AppError';
import BaseModalErrorToast from './BaseModalErrorToast';
import cn from '~modules/cn';
import CloseIcon from '../../../assets/icons/v2design/basics/close.svg';
import './base-modal.scss';

export type BaseModalSize = 'x-large' | 'large' | 'medium' | 'small' | 'x-small';

export interface IBaseModal {
    Title?: React.ReactNode;
    children?: React.ReactNode;
    Footer?: JSX.Element;
    showCloseButton?: boolean;
    size?: BaseModalSize;
    type?: 'fullWidthContent' | 'fullHeightContent' | 'leftSideContent' | 'rightSideContent';
    onClose?: () => void;
    resizableProps?: ResizableProps;
    error?: AppError;
    errorStr?: string;
    contentRef?: React.RefObject<HTMLDivElement>;
}

export function BaseModal({
    Title,
    type = 'fullWidthContent',
    size = 'small',
    children,
    onClose,
    Footer,
    showCloseButton,
    resizableProps = { direction: [] },
    error,
    contentRef,
    errorStr
}: IBaseModal): JSX.Element {
    const showHeader = showCloseButton || Title;
    return (
        <Resizable
            {...resizableProps}
            className={cn('base-modal', {
                [`base-modal--${size}`]: size,
                'base-modal--full-width': type === 'fullWidthContent',
                'base-modal--full-height': type === 'fullHeightContent',
                'base-modal--right-side-content': type === 'rightSideContent',
                'base-modal--left-side-content': type === 'leftSideContent'
            })}
        >
            {showHeader && (
                <div className="base-modal__header">
                    {Title}
                    {showCloseButton && (
                        <Button
                            buttonSize="small"
                            className="base-modal__control-button"
                            icon={<CloseIcon />}
                            buttonType="tertiary"
                            onClick={() => onClose?.()}
                        />
                    )}
                </div>
            )}
            {error && errorStr && <BaseModalErrorToast errorString={errorStr} error={error} />}
            <div ref={contentRef} className="base-modal__content">
                {children}
            </div>
            {Footer && <div className="base-modal__footer">{Footer}</div>}
        </Resizable>
    );
}
